import { Component } from "@theme/component";
import { ThemeEvents, QuantitySelectorUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { onAnimationEnd, fetchConfig } from "@theme/utilities";

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    "stickyBar",
    "addToCartButton",
    "quantityDisplay",
    "quantityNumber",
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    // Show the sticky bar always by default!
    this.#showStickyBar();

    const hideTargets = document.querySelectorAll('product-recommendations, footer');
    if (hideTargets.length > 0) {
      let intersectingCount = 0;
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              intersectingCount++;
            } else {
              intersectingCount = Math.max(0, intersectingCount - 1);
            }
          });

          if (intersectingCount > 0) {
            this.#hideStickyBar();
          } else {
            this.#showStickyBar();
          }
        },
        { rootMargin: "0px 0px 0px 0px" }
      );
      
      hideTargets.forEach(target => this.#mainBottomObserver.observe(target));
    }

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
      { signal },
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartAddComplete,
      { signal },
    );
    document.addEventListener(
      ThemeEvents.cartError,
      this.#handleCartAddComplete,
      { signal },
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal },
    );

    this.#getInitialQuantity();

    const mainForm = this.#getProductForm();
    if (mainForm && !mainForm.closest("sticky-add-to-cart")) {
      this.#targetAddToCartButton =
        mainForm.querySelector('[ref="addToCartButton"]') ||
        mainForm.querySelector('button[type="submit"]');
    } else {
      this.#targetAddToCartButton = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = "true";
      this.#targetAddToCartButton.click();

      // Instantly reset the puppet flag on a tiny timeout so subsequent clicks can be made!
      setTimeout(() => {
        if (this.#targetAddToCartButton) {
          this.#targetAddToCartButton.dataset.puppet = "false";
        }
      }, 100);

      const cartIcon = document.querySelector(".header-actions__cart-icon");

      if (this.refs.addToCartButton.dataset.added !== "true") {
        this.refs.addToCartButton.dataset.added = "true";
      }

      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute("data-added");
      }, 800);

      if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage)
        return;

      const flyToCartElement = /** @type {FlyToCart} */ (
        document.createElement("fly-to-cart")
      );
      const sourceStyles = getComputedStyle(this.refs.productImage);

      flyToCartElement.classList.add("fly-to-cart--sticky");
      flyToCartElement.style.setProperty(
        "background-image",
        `url(${this.refs.productImage.src})`,
      );
      flyToCartElement.useSourceSize = "true";
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);

      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    } else {
      // Fallback checkout submission!
      const button = this.refs.addToCartButton;
      button.disabled = true;
      button.classList.add("loading");

      const variantId = this.dataset.currentVariantId;
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", this.#currentQuantity.toString());

      // Get sections to update so the drawer morphs and updates perfectly
      const cartItemsComponents = document.querySelectorAll(
        "cart-items-component",
      );
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });
      if (cartItemComponentsSectionIds.length > 0) {
        formData.append("sections", cartItemComponentsSectionIds.join(","));
      }

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
        });
        const cartData = await response.json();

        // Dispatch event with the exact required schema to update the cart drawer
        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              resource: cartData,
              sourceId: variantId.toString(),
              data: {
                sections: cartData.sections,
                source: "product-form-component",
                itemCount: this.#currentQuantity,
                productId: this.dataset.productId,
              },
            },
          }),
        );

        // Trigger success animation on button
        if (button.dataset.added !== "true") {
          button.dataset.added = "true";
        }
        setTimeout(() => {
          button.removeAttribute("data-added");
        }, 800);
      } catch (error) {
        console.error("Add to Cart failed:", error);
      } finally {
        button.disabled = false;
        button.classList.remove("loading");
      }
    }
  };

  /**
   * Handles the buy now click in the sticky bar
   */
  handleBuyNowClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const variantId = this.dataset.currentVariantId;
    if (!variantId) return;

    // Use event.target.closest('button') to safely support event delegation!
    const button = event.target.closest("button");
    if (!button) return;

    button.disabled = true;
    button.classList.add("loading");

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", this.#currentQuantity.toString());

    try {
      await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      });
      window.location.href = "/checkout";
    } catch (error) {
      console.error("Buy Now failed:", error);
      button.disabled = false;
      button.classList.remove("loading");
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart =
      event.detail.data.html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck =
      this.refs.stickyBar.getAttribute("data-stuck") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]',
      );
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(
      ".sticky-add-to-cart__variant",
    );
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`,
    );
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll("input:checked"),
    )
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== "")
      .join(" / ");
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = "false";
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = "true";
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = "false";
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");

    // Find all matching forms in the section
    const forms = document.querySelectorAll(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );

    // Return the first one that is NOT inside sticky-add-to-cart
    for (const form of forms) {
      if (!form.closest("sticky-add-to-cart")) {
        return form;
      }
    }

    // Fallback: return the one inside ourselves
    return this.querySelector("product-form-component");
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = "inline";
    } else {
      quantityDisplay.style.display = "none";
    }
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
}
